import { ElementType, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../../utils/cn'
import IconCard from '../../Icons/IconCard'
import IconManualBankTransfer from '../../Icons/IconManualBankTransfer'

export type CompanyPaymentMethod = 'card' | 'bank_transfer'

interface Props {
  /**
   * Current method, derived from the organisation's subscription. Undefined
   * while unknown - neither option is marked rather than guessing one.
   *
   * Read-only by design: switching between card and bank transfer is not
   * supported yet, and no endpoint accepts a new value. Rendering this as an
   * editable control would silently discard the choice.
   */
  value?: CompanyPaymentMethod
}

/**
 * The authoritative bank details live in the backend's BANK_TRANSFER_DETAILS and
 * are emailed to the customer on signup; no endpoint exposes them, so they are
 * duplicated into this variable. Re-confirmed against reportall-back b1bc31d:
 * only mail/utils.py reads the setting, and no route serves it.
 */
const bankDetails = process.env.REACT_APP_SUBSCRIPTION_BANK_DETAILS?.trim()

/**
 * Gates the whole section, not just the toggle below - see `CompanySettings`.
 * Without the details this screen is two read-only tiles naming a method Plan &
 * Billing already states, drawn with ticked checkboxes that ignore every click
 * because no endpoint accepts a new value. Set the variable and the section
 * earns its place by carrying the details the customer needs to pay.
 */
export const hasSubscriptionBankDetails = Boolean(bankDetails)

const CompanyPaymentOptions = ({ value }: Props) => {
  const { t } = useTranslation()
  const [areBankDetailsVisible, setAreBankDetailsVisible] = useState(false)

  return (
    <div>
      <h2 className='font-bold text-2xl text-[#3D3D3D]'>
        {t('paymentOptions')}
      </h2>
      <p className='mt-1 text-lightGray'>{t('managePaymentMethod')}</p>

      <div className='mt-6 grid gap-5 sm:grid-cols-2 max-w-[52.9375rem]'>
        <PaymentOption
          icon={IconCard}
          label={t('cardPayment')}
          isSelected={value === 'card'}
        />
        <PaymentOption
          icon={IconManualBankTransfer}
          label={t('manualBankTransfer')}
          isSelected={value === 'bank_transfer'}
        />
      </div>

      {bankDetails && (
        <>
          <div className='max-w-[52.9375rem] flex justify-end mt-3'>
            <button
              type='button'
              onClick={() => setAreBankDetailsVisible((prev) => !prev)}
              className='text-primaryBlue font-semibold text-sm hover:underline'
            >
              {t(
                areBankDetailsVisible
                  ? 'hideSubscriptionBankDetails'
                  : 'showSubscriptionBankDetails',
              )}
            </button>
          </div>

          {areBankDetailsVisible && (
            <div className='max-w-[52.9375rem] mt-3 rounded-lg border-[1px] border-solid border-borderGray p-5 text-sm text-[#3D3D3D] whitespace-pre-line'>
              {bankDetails}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default CompanyPaymentOptions

function PaymentOption({
  icon,
  label,
  isSelected,
}: {
  icon: ElementType<{ className?: string }>
  label: string
  isSelected: boolean
}) {
  const Icon = icon
  return (
    <div
      aria-current={isSelected}
      className={cn(
        'border-[1px] border-solid rounded-lg px-5 py-4 flex items-center gap-4 w-full',
        isSelected ? 'border-primaryBlue' : 'border-borderGray',
      )}
    >
      <Icon />
      <span className='text-base font-semibold text-black flex-1 text-left'>
        {label}
      </span>
      <span
        className={cn(
          'w-5 h-5 rounded-[4px] border-[1px] border-solid flex items-center justify-center shrink-0',
          isSelected
            ? 'bg-primaryBlue border-primaryBlue'
            : 'bg-white border-borderGray',
        )}
      >
        {isSelected && (
          <svg width='12' height='9' viewBox='0 0 12 9' fill='none'>
            <path
              d='M1 4.5L4.5 8L11 1'
              stroke='white'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        )}
      </span>
    </div>
  )
}
